import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { TrendingUp, PieChart as PieIcon, BarChart3, FileText, IndianRupee } from "lucide-react";
import { PIE_COLORS, formatCurrency, formatDateIST, formatMode } from "./pharmacyConstants";

export function PharmacyCharts({ charts, chartView, setChartView }) {
  if (!charts) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="charts-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-slate-800">Analytics</h2>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {[
            { key: "trend", icon: TrendingUp, label: "Trend" },
            { key: "status", icon: PieIcon, label: "Status" },
            { key: "patient", icon: BarChart3, label: "Patient Type" },
            { key: "scheme", icon: FileText, label: "Scheme" },
            { key: "paymode", icon: IndianRupee, label: "Pay Mode" },
          ].map(t => (
            <button key={t.key} onClick={() => setChartView(t.key)} data-testid={`chart-tab-${t.key}`}
              className={`px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${chartView === t.key ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[280px]">
        {chartView === "trend" && charts.revenue_trend.length > 0 && (
          <ResponsiveContainer>
            <AreaChart data={charts.revenue_trend}>
              <defs>
                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0d9488" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => [formatCurrency(v), "Revenue"]} labelFormatter={v => formatDateIST(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#0d9488" strokeWidth={2} fill="url(#colorRev)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {chartView === "status" && charts.status_distribution.length > 0 && (
          <ResponsiveContainer>
            <PieChart>
              <Pie data={charts.status_distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                dataKey="amount" nameKey="label" paddingAngle={3}>
                {charts.status_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        )}
        {chartView === "patient" && charts.patient_type.length > 0 && (
          <ResponsiveContainer>
            <BarChart data={charts.patient_type}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="type" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Bar dataKey="paid" name="Paid" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="pending" name="Pending" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        )}
        {chartView === "scheme" && charts.scheme_analysis && charts.scheme_analysis.length > 0 && (
          <ResponsiveContainer>
            <BarChart data={charts.scheme_analysis} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Bar dataKey="paid" name="Paid" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="revenue" name="Total" stackId="b" fill="#0d9488" radius={[0, 4, 4, 0]} />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        )}
        {chartView === "paymode" && charts.payment_modes && charts.payment_modes.length > 0 && (() => {
          const merged = charts.payment_modes.reduce((acc, m) => {
            const label = formatMode(m.mode);
            const existing = acc.find(a => a.label === label);
            if (existing) { existing.amount += m.amount; existing.count += m.count; }
            else acc.push({ label, amount: m.amount, count: m.count });
            return acc;
          }, []);
          return (
          <ResponsiveContainer>
            <PieChart>
              <Pie data={merged} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                dataKey="amount" nameKey="label" paddingAngle={3}>
                {merged.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
          );
        })()}
        {((chartView === "trend" && (!charts.revenue_trend || charts.revenue_trend.length === 0)) ||
          (chartView === "status" && (!charts.status_distribution || charts.status_distribution.length === 0)) ||
          (chartView === "patient" && (!charts.patient_type || charts.patient_type.length === 0)) ||
          (chartView === "scheme" && (!charts.scheme_analysis || charts.scheme_analysis.length === 0)) ||
          (chartView === "paymode" && (!charts.payment_modes || charts.payment_modes.length === 0))) && (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">No chart data</div>
        )}
      </div>
    </div>
  );
}
